import {
  ButtonGroup,
  MenuItem,
  ClickAwayListener,
  Paper,
  Popper,
  Grow,
  MenuList,
  Divider,
  Stack,
} from '@mui/material';
import { useRef, useState } from 'react';
import {
  Control,
  useWatch,
  UseFormSetValue,
  FieldErrors,
} from 'react-hook-form';
import {
  IconShoppingCart,
  IconSend,
  IconChevronDown,
} from '@tabler/icons-react';
import {
  PurchaseRequestGroupSchemaLite,
  PurchaseRequestGroupType,
} from 'types/clientV2/purchaseRequests';
import {
  formConstants,
  addToPurchaseRequestGroupFormFormInputData,
} from 'ui-component/AddToCartPopover/constants';
import { HookFormInput } from 'ui-component/HookFormComponents';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';

type PurchaseRequestGroupLite = Pick<
  PurchaseRequestGroupSchemaLite,
  'id' | 'name' | 'createdAt'
>;

type PurchaseRequestGroupSelectionProps = {
  control: Control<addToPurchaseRequestGroupFormFormInputData>;
  setValue: UseFormSetValue<addToPurchaseRequestGroupFormFormInputData>;
  errors: FieldErrors<addToPurchaseRequestGroupFormFormInputData>;
  activeGroups: PurchaseRequestGroupLite[];
  mostRecentGroup: PurchaseRequestGroupLite | null;
  isLoading: boolean;
  hasActiveGroups: boolean;
  defaultGroupName: string;
  isSubmitting: boolean;
  type: PurchaseRequestGroupType;
};

export const getTypeConfig = (type: PurchaseRequestGroupType) => {
  if (type === PurchaseRequestGroupType.RFQ) {
    return {
      icon: IconSend,
      label: 'RFQ',
      actionLabel: 'Add to RFQ',
      newLabel: 'New RFQ Name',
      createLabel: '+ Create New RFQ',
      trackingPrefix: 'Add to RFQ',
      menuId: 'rfq-menu',
    };
  }
  return {
    icon: IconShoppingCart,
    label: 'Cart',
    actionLabel: 'Add to Cart',
    newLabel: 'New Cart Name',
    createLabel: '+ Create New Cart',
    trackingPrefix: 'Add to Cart',
    menuId: 'cart-menu',
  };
};

export const PurchaseRequestGroupSelection = ({
  control,
  setValue,
  errors,
  activeGroups,
  mostRecentGroup,
  isLoading,
  hasActiveGroups,
  defaultGroupName,
  isSubmitting,
  type,
}: PurchaseRequestGroupSelectionProps) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const typeConfig = getTypeConfig(type);
  const Icon = typeConfig.icon;

  const selectedGroupId = useWatch({
    control,
    name: formConstants.purchaseRequestGroupId.id,
  });

  const groupName = useWatch({
    control,
    name: formConstants.cartName.id,
  });

  const isCreatingNew = !selectedGroupId && !!groupName;

  const selectedGroup = activeGroups.find(
    (group) => group.id === selectedGroupId
  );

  const handleToggle = () => {
    setDropdownOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setDropdownOpen(false);
  };

  const handleGroupSelect = (groupId: string) => {
    setValue(formConstants.purchaseRequestGroupId.id, groupId);
    setValue(formConstants.cartName.id, null);
    setDropdownOpen(false);
  };

  const handleCreateNew = () => {
    setValue(formConstants.purchaseRequestGroupId.id, null);
    setValue(formConstants.cartName.id, defaultGroupName);
    setDropdownOpen(false);
  };

  const getButtonText = () => {
    if (isCreatingNew) {
      const name = groupName || defaultGroupName;
      return `Add to ${name}`;
    }
    if (selectedGroup) {
      return `Add to ${selectedGroup.name}`;
    }
    return typeConfig.actionLabel;
  };

  if (!hasActiveGroups || isCreatingNew) {
    return (
      <Stack spacing={2}>
        <HookFormInput
          name={formConstants.cartName.id}
          label={typeConfig.newLabel}
          control={control}
          errors={errors}
          type="text"
          size="small"
        />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <SegmentLoadingButton
            type="submit"
            variant="contained"
            loading={isLoading || isSubmitting}
            startIcon={<Icon size={20} />}
            trackingName={`${typeConfig.trackingPrefix} - Submit`}
          >
            {getButtonText()}
          </SegmentLoadingButton>
        </Stack>
      </Stack>
    );
  }

  const otherGroups = activeGroups.filter(
    (group) => group.id !== selectedGroupId
  );

  return (
    <Stack direction="row" spacing={2} justifyContent="flex-end">
      <ButtonGroup variant="contained" ref={anchorRef}>
        <SegmentLoadingButton
          type="submit"
          variant="contained"
          loading={isLoading || isSubmitting}
          startIcon={<Icon size={20} />}
          trackingName={`${typeConfig.trackingPrefix} - Submit`}
        >
          {getButtonText()}
        </SegmentLoadingButton>
        <SegmentButton
          aria-controls={dropdownOpen ? typeConfig.menuId : undefined}
          aria-expanded={dropdownOpen ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
          sx={{
            px: '5px',
            minWidth: '20px',
            height: '100%',
            '&.MuiButtonGroup-grouped': {
              px: '5px',
              minWidth: '20px',
              height: '100%',
            },
          }}
          trackingName={`${typeConfig.trackingPrefix} - Toggle Dropdown`}
        >
          <IconChevronDown size={20} />
        </SegmentButton>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1400 }}
        open={dropdownOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal={false}
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id={typeConfig.menuId} autoFocusItem>
                  {otherGroups.map((group) => (
                    <MenuItem
                      key={group.id}
                      selected={false}
                      onClick={() => handleGroupSelect(group.id)}
                    >
                      {group.name}
                    </MenuItem>
                  ))}
                  {otherGroups.length > 0 && <Divider />}
                  <MenuItem onClick={handleCreateNew}>
                    {typeConfig.createLabel}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Stack>
  );
};
